import React from 'react';
import PropTypes from 'prop-types';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import { Box, Chip, FormHelperText, Typography, makeStyles, withStyles } from '@material-ui/core';
import { Controller, useFieldArray } from 'react-hook-form';
import NumberInput from '../../Common/Form/NumberInput';

const propTypes = {
  name: PropTypes.string.isRequired,
  kobeZones: PropTypes.arrayOf(PropTypes.string).isRequired,
  formMethods: PropTypes.shape({
    control: PropTypes.object,
    errors: PropTypes.object,
    trigger: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
};
const defaultProps = {};

const useStyles = makeStyles(theme => ({
  chipDisabled: {
    background: '#e7cbec',
    color: theme.palette.primary.main,
    '&:hover': {
      background: '#e7cbec',
    },
    '&:focus': {
      background: '#e7cbec',
    },
  },
}));

const RemoveCircleIcon = withStyles(theme => ({
  root: {
    background: theme.palette.primary.main,
    borderRadius: '50%',
    color: '#fff',
  },
}))(RemoveCircle);

function KobeZoneFields({ name, kobeZones, formMethods }) {
  const classes = useStyles();
  const { control, errors, trigger, watch } = formMethods;
  const { fields, replace, remove } = useFieldArray({ control, name });
  const hasKobeZones = kobeZones.length > 0;
  const currentKobeZones = watch(name);
  return (
    <Box display="flex" flexDirection="column" gridGap="16px" width="100%">
      {hasKobeZones ? (
        <Box display="flex" flexDirection="row" gridGap="8px" flexWrap="wrap">
          {kobeZones.map(option => {
            const zoneIndex = (currentKobeZones ?? []).findIndex(zone => zone.zone === option);
            const isSelected = zoneIndex !== -1;
            const addZone = () => {
              const newZone = { zone: option, ceiling: null, floor: null };
              const newValues = [...(currentKobeZones ?? []), newZone].sort((a, b) => a.zone.localeCompare(b.zone));
              replace(newValues);
            };
            const removeZone = () => remove(zoneIndex);
            return (
              <Chip
                key={option}
                classes={{ root: isSelected ? classes.chipDisabled : undefined }}
                color="primary"
                icon={isSelected ? <RemoveCircleIcon /> : <AddCircle />}
                label={isSelected ? `Remove ${option}` : `Add ${option}`}
                size="small"
                onClick={isSelected ? removeZone : addZone}
              />
            );
          })}
        </Box>
      ) : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gridGap: '16px' }}>
        {fields.map((item, index) => {
          const error = Boolean(
            errors?.kobe_zones?.[index]?.floor?.message || errors?.kobe_zones?.[index]?.ceiling?.message
          );
          const helperText =
            errors?.kobe_zones?.[index]?.floor?.message || errors?.kobe_zones?.[index]?.ceiling?.message;

          return (
            <Box key={item.id} sx={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1 / 4' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1 / 4' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">{item.zone}</Typography>
                </Box>

                <Controller
                  name={`kobe_zones[${index}].floor`}
                  control={control}
                  render={({ field: { ref, value, onChange, ...rest } }) => (
                    <NumberInput
                      disabled={!hasKobeZones}
                      error={Boolean(errors?.kobe_zones?.[index]?.floor?.message)}
                      label="Floor"
                      min={0}
                      value={value === null ? '' : value}
                      onChange={strValue => {
                        const newValue = strValue === '' ? null : Number(strValue);
                        onChange(newValue);
                        trigger(`kobe_zones[${index}].ceiling`);
                      }}
                      {...rest}
                    />
                  )}
                />

                <Controller
                  name={`kobe_zones[${index}].ceiling`}
                  control={control}
                  render={({ field: { ref, value, onChange, ...rest } }) => (
                    <NumberInput
                      disabled={!hasKobeZones}
                      error={Boolean(errors?.kobe_zones?.[index]?.ceiling?.message)}
                      label="Ceiling"
                      min={0}
                      value={value === null ? '' : value}
                      onChange={strValue => {
                        const newValue = strValue === '' ? null : Number(strValue);
                        onChange(newValue);
                        trigger(`kobe_zones[${index}].floor`);
                      }}
                      {...rest}
                    />
                  )}
                />
              </Box>

              {helperText ? (
                <Box sx={{ display: 'grid', gridColumn: '1 / 4' }}>
                  <FormHelperText error={error}>{helperText}</FormHelperText>
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

KobeZoneFields.propTypes = propTypes;
KobeZoneFields.defaultProps = defaultProps;

export default KobeZoneFields;
